/**
 * VistoeraTASK-00523 - the ONE folder-tree UI used by the Canvas Image Asset
 * Picker and the Image Asset Center. Purely presentational: callers own data +
 * mutations, so both entrances render the same canonical tree.
 */
import { useEffect, useMemo, useState } from 'react'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import {
  CaretDownOutlined,
  CaretRightOutlined,
  EllipsisOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
  ancestorIds,
  buildFolderForest,
  flattenVisible,
  folderPath,
  type FolderLike,
} from './folderTreeModel'
import './image-folder-tree.css'

/** Indentation stops growing after this depth so a 15-level tree can never widen the panel. */
const MAX_INDENT_LEVELS = 8
const INDENT_PX = 14

export interface ImageFolderTreeFolder extends FolderLike {
  accessible_asset_count?: number
}

export interface ImageFolderTreeProps<T extends ImageFolderTreeFolder> {
  folders: readonly T[]
  selectedFolderId: string | null
  onSelect: (folderId: string) => void
  /** Owner/Admin only - hides the create / rename / delete affordances otherwise. */
  canManage: boolean
  onCreate: (parentId: string | null) => void
  onRename: (folder: T) => void
  onDelete: (folder: T) => void
  testIdPrefix?: string
}

export default function ImageFolderTree<T extends ImageFolderTreeFolder>({
  folders,
  selectedFolderId,
  onSelect,
  canManage,
  onCreate,
  onRename,
  onDelete,
  testIdPrefix = 'ift',
}: ImageFolderTreeProps<T>) {
  const { t } = useTranslation('imageAssets')
  const forest = useMemo(() => buildFolderForest(folders), [folders])
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  // Keep the current folder visible: expand every ancestor (and itself) when the
  // selection changes - e.g. after a breadcrumb jump or a deep-link restore.
  useEffect(() => {
    if (!selectedFolderId) return
    const need = [...ancestorIds(folders, selectedFolderId), selectedFolderId]
    setExpanded((prev) => {
      if (need.every((id) => prev.has(id))) return prev
      const next = new Set(prev)
      need.forEach((id) => next.add(id))
      return next
    })
  }, [folders, selectedFolderId])

  const rows = useMemo(() => flattenVisible(forest, expanded), [forest, expanded])

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="ift-root" data-testid={`${testIdPrefix}-root`}>
      <div className="ift-head">
        <span className="ift-head-title">{t('folders.treeTitle')}</span>
        {canManage ? (
          <button
            type="button"
            className="ift-icon-btn"
            onClick={() => onCreate(null)}
            aria-label={t('folders.createRoot')}
            title={t('folders.createRoot')}
            data-testid={`${testIdPrefix}-create-root`}
          >
            <PlusOutlined />
          </button>
        ) : null}
      </div>
      <div className="ift-tree" role="tree" aria-label={t('folders.treeAria')} data-testid={`${testIdPrefix}-tree`}>
        {rows.length === 0 ? <div className="ift-empty">{t('folders.emptyTree')}</div> : null}
        {rows.map(({ folder, depth, hasChildren, expanded: isOpen }) => {
          const selected = folder.id === selectedFolderId
          const fullPath = folderPath(folders, folder.id)
            .map((f) => f.name)
            .join(' / ')
          const menu: MenuProps['items'] = [
            { key: 'child', label: t('folders.createChild'), onClick: () => onCreate(folder.id) },
            { key: 'rename', label: t('folders.rename'), onClick: () => onRename(folder) },
            { key: 'delete', label: t('folders.delete'), danger: true, onClick: () => onDelete(folder) },
          ]
          return (
            <div
              key={folder.id}
              role="treeitem"
              aria-level={depth + 1}
              aria-selected={selected}
              aria-expanded={hasChildren ? isOpen : undefined}
              className={`ift-row${selected ? ' is-selected' : ''}`}
              style={{ paddingLeft: 6 + Math.min(depth, MAX_INDENT_LEVELS) * INDENT_PX }}
              data-testid={`${testIdPrefix}-row`}
              data-folder-id={folder.id}
              data-depth={depth}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className="ift-caret"
                  onClick={() => toggle(folder.id)}
                  aria-label={t(isOpen ? 'folders.collapseAria' : 'folders.expandAria', { name: folder.name })}
                  data-testid={`${testIdPrefix}-caret`}
                >
                  {isOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </button>
              ) : (
                <span className="ift-caret ift-caret-spacer" aria-hidden />
              )}
              <button
                type="button"
                className="ift-main"
                title={fullPath}
                onClick={() => onSelect(folder.id)}
                data-testid={`${testIdPrefix}-select`}
              >
                <span className="ift-folder-icon">{isOpen || selected ? <FolderOpenOutlined /> : <FolderOutlined />}</span>
                <span className="ift-name">{folder.name}</span>
                {typeof folder.accessible_asset_count === 'number' ? (
                  <span className="ift-count">{folder.accessible_asset_count}</span>
                ) : null}
              </button>
              {canManage ? (
                <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                  <button
                    type="button"
                    className="ift-more"
                    aria-label={t('folders.manageAria', { name: folder.name })}
                    data-testid={`${testIdPrefix}-more`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EllipsisOutlined />
                  </button>
                </Dropdown>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
